export const FLOATING_JOYSTICK_DEFAULTS = Object.freeze({
  maxRadius: 42,
  deadzone: 9,
  dragThreshold: 12,
  lingerMs: 1400,
  tapMaxDistance: 12,
  tapMaxDurationMs: 450,
  reacquireRadius: 54
})

function distanceBetween(x1, y1, x2, y2) {
  return Math.hypot(x2 - x1, y2 - y1)
}

function isFinitePoint(x, y) {
  return Number.isFinite(x) && Number.isFinite(y)
}

export class FloatingJoystick {
  constructor(options = {}) {
    this.options = {
      ...FLOATING_JOYSTICK_DEFAULTS,
      ...options
    }

    if (
      this.options.maxRadius <= 0 ||
      this.options.deadzone < 0 ||
      this.options.deadzone >= this.options.maxRadius
    ) {
      throw new Error('Floating joystick requires 0 <= deadzone < maxRadius')
    }

    this.reset()
  }

  begin(pointerId, x, y, now = 0) {
    this.advance(now)
    if (pointerId == null || !isFinitePoint(x, y) || this.pointers.has(pointerId)) {
      return { handled: false }
    }

    let mode = 'action'
    const canReacquire = (
      this.visible &&
      this.anchor &&
      this.movementPointerId == null
    )

    if (canReacquire) {
      mode = 'joystick'
      this.movementPointerId = pointerId
      this.hideAt = null
    } else if (!this.visible && this.anchorCandidateId == null && this.movementPointerId == null) {
      mode = 'anchor'
      this.anchorCandidateId = pointerId
    } else if (this.visible) {
      this.hideAt = null
    }

    this.pointers.set(pointerId, {
      mode,
      startX: x,
      startY: y,
      x,
      y,
      startedAt: now,
      moved: false
    })

    if (mode === 'joystick') {
      this.updateMovement(x, y)
    }

    return { handled: true, mode }
  }

  move(pointerId, x, y, now = 0) {
    const pointer = this.pointers.get(pointerId)
    if (!pointer || !isFinitePoint(x, y)) return { handled: false }

    pointer.x = x
    pointer.y = y
    const travel = distanceBetween(pointer.startX, pointer.startY, x, y)
    if (travel > this.options.tapMaxDistance) {
      pointer.moved = true
    }

    if (pointer.mode === 'joystick') {
      this.updateMovement(x, y)
      return { handled: true, mode: 'joystick', anchored: false }
    }

    if (pointer.mode === 'anchor' && travel >= this.options.dragThreshold) {
      pointer.mode = 'joystick'
      this.anchorCandidateId = null
      this.movementPointerId = pointerId
      this.anchor = { x: pointer.startX, y: pointer.startY }
      this.visible = true
      this.hideAt = null
      this.updateMovement(x, y)
      return { handled: true, mode: 'joystick', anchored: true }
    }

    return { handled: true, mode: pointer.mode, anchored: false }
  }

  end(pointerId, x, y, now = 0) {
    const pointer = this.pointers.get(pointerId)
    if (!pointer) return { handled: false, tap: false }

    if (isFinitePoint(x, y)) {
      pointer.x = x
      pointer.y = y
    }

    const wasJoystick = pointer.mode === 'joystick'
    const travel = distanceBetween(
      pointer.startX,
      pointer.startY,
      pointer.x,
      pointer.y
    )
    const duration = Math.max(0, now - pointer.startedAt)
    const tap = (
      !wasJoystick &&
      !pointer.moved &&
      travel <= this.options.tapMaxDistance &&
      duration <= this.options.tapMaxDurationMs
    )

    this.pointers.delete(pointerId)

    if (this.anchorCandidateId === pointerId) {
      this.anchorCandidateId = null
    }

    if (this.movementPointerId === pointerId) {
      this.movementPointerId = null
      this.zeroMovement()
    }

    this.scheduleHide(now)
    return { handled: true, tap, mode: pointer.mode }
  }

  cancel(pointerId, now = 0) {
    const pointer = this.pointers.get(pointerId)
    if (!pointer) return { handled: false }

    this.pointers.delete(pointerId)

    if (this.anchorCandidateId === pointerId) {
      this.anchorCandidateId = null
    }

    if (this.movementPointerId === pointerId) {
      this.movementPointerId = null
      this.zeroMovement()
    }

    this.scheduleHide(now)
    return { handled: true }
  }

  keepAlive(now = 0) {
    if (!this.visible || this.movementPointerId != null) return false
    this.hideAt = now + this.options.lingerMs
    return true
  }

  advance(now = 0) {
    if (
      this.visible &&
      this.movementPointerId == null &&
      this.pointers.size === 0 &&
      this.hideAt != null &&
      now >= this.hideAt
    ) {
      this.hide()
      return true
    }

    return false
  }

  reset() {
    this.pointers = new Map()
    this.anchorCandidateId = null
    this.movementPointerId = null
    this.anchor = null
    this.knob = null
    this.vector = { x: 0, y: 0 }
    this.visible = false
    this.hideAt = null
  }

  snapshot() {
    return {
      visible: this.visible,
      active: this.movementPointerId != null,
      pending: this.pointers.size > 0,
      anchor: this.anchor ? { ...this.anchor } : null,
      knob: this.knob ? { ...this.knob } : null,
      vector: { ...this.vector },
      hideAt: this.hideAt
    }
  }

  scheduleHide(now) {
    if (!this.visible || this.movementPointerId != null || this.pointers.size > 0) {
      return
    }
    this.hideAt = now + this.options.lingerMs
  }

  updateMovement(x, y) {
    if (!this.anchor) return

    const dx = x - this.anchor.x
    const dy = y - this.anchor.y
    const distance = Math.hypot(dx, dy)
    const directionX = distance > 0 ? dx / distance : 0
    const directionY = distance > 0 ? dy / distance : 0
    const clampedDistance = Math.min(distance, this.options.maxRadius)

    this.knob = {
      x: this.anchor.x + directionX * clampedDistance,
      y: this.anchor.y + directionY * clampedDistance
    }

    if (distance <= this.options.deadzone) {
      this.vector = { x: 0, y: 0 }
      return
    }

    const strength = Math.min(
      1,
      (clampedDistance - this.options.deadzone) /
        (this.options.maxRadius - this.options.deadzone)
    )
    this.vector = {
      x: directionX * strength,
      y: directionY * strength
    }
  }

  zeroMovement() {
    this.vector = { x: 0, y: 0 }
    this.knob = this.anchor ? { ...this.anchor } : null
  }

  hide() {
    this.anchor = null
    this.knob = null
    this.vector = { x: 0, y: 0 }
    this.visible = false
    this.hideAt = null
  }
}
